import { useState, lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Star, CheckCircle, ChevronDown, ArrowRight, ShieldCheck, FileCheck } from "lucide-react";
import calendrierWhiteIcon from "@/assets/icons/calendrier-white.webp";
import { SiteLayout } from "@/components/site/SiteLayout";
import { TrustvilleBadge } from "@/components/site/TrustvilleBadge";
import { SEOHead } from "@/components/SEOHead";
import { LocalBusinessSchema, WebSiteSchema, FAQSchema } from "@/components/SchemaOrg";
import heroAutosur from "@/assets/centre/hero-autosur.webp";
import facadeCentre from "@/assets/centre/facade-centre.webp";

// Lazy load heavy below-fold components
const CTASection = lazy(() => import("@/components/site/CTASection").then(m => ({ default: m.CTASection })));
const LocationSection = lazy(() => import("@/components/site/LocationSection").then(m => ({ default: m.LocationSection })));
const ArticleCard = lazy(() => import("@/components/blog/ArticleCard").then(m => ({ default: m.ArticleCard })));
import { TestimonialsCarousel } from "@/components/site/TestimonialsCarousel";
import { articles } from "@/data/blogContent";
// Custom vehicle icons (blue for the grid)
import voitureBlueIcon from "@/assets/icons/voiture-blue.webp";
import utilitaireBlueIcon from "@/assets/icons/utilitaire-blue.webp";
import campingcarBlueIcon from "@/assets/icons/campingcar-blue.webp";
import collectionBlueIcon from "@/assets/icons/collection-blue.webp";
import gplBlueIcon from "@/assets/icons/gpl-blue.webp";
import quatreQuatreBlueIcon from "@/assets/icons/4x4-blue.webp";
import motoBlueIcon from "@/assets/icons/moto-blue.webp";
// Icons for advantages
import calendrierBlueIcon from "@/assets/icons/calendrier-blue.webp";
import calendrierRedIcon from "@/assets/icons/calendrier-red.webp";
import garageRedIcon from "@/assets/icons/garage-red.webp";
import vanRedIcon from "@/assets/icons/van-red.webp";
import voitureRedIcon from "@/assets/icons/voiture-red.webp";
// Marque préférée banner removed - now in LocationSection

const vehicleTypes = [
  { 
    iconSrc: motoBlueIcon, 
    title: "2/3 Roues", 
    description: "Motos, quads, voiturettes",
    href: "/vehicules/motos" 
  },
  { 
    iconSrc: collectionBlueIcon, 
    title: "Collection", 
    description: "Anciennes et exception",
    href: "/vehicules/collection" 
  },
  { 
    iconSrc: voitureBlueIcon, 
    title: "Voiture", 
    description: "Véhicules particuliers",
    href: "/vehicules/voitures" 
  },
  { 
    iconSrc: quatreQuatreBlueIcon, 
    title: "Véhicule 4x4", 
    description: "SUV et tout-terrain",
    href: "/vehicules/voitures" 
  },
  { 
    iconSrc: utilitaireBlueIcon, 
    title: "Utilitaire", 
    description: "Fourgons, camionnettes",
    href: "/vehicules/utilitaires" 
  },
  { 
    iconSrc: campingcarBlueIcon, 
    title: "Spécifique", 
    description: "Camping-cars, aménagés",
    href: "/vehicules/utilitaires" 
  },
  { 
    iconSrc: gplBlueIcon, 
    title: "Véhicule GPL", 
    description: "GPL / GNV",
    href: "/prestations" 
  },
];

const services = [
  "Contrôle technique obligatoire",
  "Contre-visite",
  "Contrôle pollution",
  "Véhicules hybrides/électriques",
  "Véhicules GPL/Gaz",
  "Catégorie L (2 roues, quads)",
  "Pré-contrôle volontaire",
  "Véhicules de collection",
];

const advantages: { iconSrc?: string; LucideIcon?: React.ComponentType<any>; title: string; description: string; scale?: number }[] = [
  { iconSrc: calendrierRedIcon, title: "RDV en ligne 24/7", description: "Réservez à 3h du matin si vous voulez. Ou venez le samedi sans RDV selon disponibilités.", scale: 0.85 },
  { LucideIcon: ShieldCheck, title: "Détection complète", description: "Freins, suspension, éclairage, pollution... Notre CT identifie TOUS les points faibles de votre véhicule." },
  { LucideIcon: FileCheck, title: "Résultat immédiat", description: "Pas d'attente. Résultat en 30 min. Procès-verbal officiel à télécharger directement." },
  { iconSrc: garageRedIcon, title: "Tarifs transparents", description: "Dès 73€ en ligne (au lieu de 80€). Pas de frais cachés. Économisez 7€ en réservant en ligne.", scale: 0.85 },
];

const homeTestimonials = [
  { author: "Traore Makan", city: "Montreuil-sous-Bois", text: "Le meilleur contrôle technique de Montreuil, professionnel ponctuel il est au top. N'hésitez pas à passer chez lui, vous n'allez pas le regretter.", rating: 5, date: "2026-01-11" },
  { author: "Valérie", city: "Montreuil-sous-Bois", text: "Enfin de vrais professionnels. Allez-y sans réserve, l'accueil y est très chaleureux, le contrôle parfait avec de vrais conseils de pro.", rating: 5, date: "2025-08-20" },
  { author: "Zak Zak", city: "Montreuil-sous-Bois", text: "Je suis tombé sur des professionnels qui ont su me rassurer, qui m'ont bien expliqué tout ce qui allait et n'allait pas sur ma voiture. On est accueilli avec le café et surtout avec bienveillance.", rating: 5, date: "2025-03-15" },
  { author: "Sylvie Martin", city: "Montreuil-sous-Bois", text: "Centre au top ! Équipe très professionnelle, accueil chaleureux et contrôle rapide. Je recommande les yeux fermés.", rating: 5, date: "2025-10-18" },
  { author: "Karim Benziane", city: "Montreuil-sous-Bois", text: "Ça fait 10 ans que je viens ici pour mes véhicules. Toujours ponctuel, toujours pro, et des prix imbattables. Le meilleur centre du 93.", rating: 5, date: "2025-07-22" },
  { author: "Nathalie Dupont", city: "Montreuil-sous-Bois", text: "Première visite et très agréablement surprise. Prise en charge rapide, explications claires sur les points de contrôle. J'y retournerai sans hésiter.", rating: 5, date: "2025-05-30" },
];

const HomePage = () => {
  const [showAllVehicles, setShowAllVehicles] = useState(false);
  
  return (
    <SiteLayout>
      <SEOHead
        title="Contrôle Technique Montreuil-sous-Bois | RDV 24/7 | AUTOSUR"
        description="Centre agréé AUTOSUR à Montreuil-sous-Bois. Voiture, moto, utilitaire dès 73€. RDV en ligne 24/7, résultat immédiat. 343 avis ⭐ 4.9/5."
        path="/"
      />
      <LocalBusinessSchema />
      <WebSiteSchema />
      <FAQSchema items={[
        { question: "Quels documents apporter pour mon CT ?", answer: "Vous devez apporter : carte grise (certificat d'immatriculation) et votre permis de conduire. Pour une contre-visite, apportez également l'ancien procès-verbal de contrôle technique." },
        { question: "Combien de temps dure un contrôle technique ?", answer: "Un CT prend généralement 30 à 45 minutes pour une voiture, 20-30 min pour une moto. Résultat immédiat avec procès-verbal officiel." },
        { question: "Quels défauts vont-ils trouver ?", answer: "Nous inspectons 133 points réglementaires : freins, suspension, éclairage, pollution, pneus, vitres, ceintures de sécurité, etc." },
        { question: "Que faire si j'échoue mon CT ?", answer: "Vous avez 2 mois pour faire une contre-visite. Celle-ci ne porte que sur les points défaillants. Contre-visite client centre à partir de 30€." },
        { question: "Puis-je faire un CT sans rendez-vous ?", answer: "Oui ! Vous pouvez venir le samedi sans RDV selon disponibilités. Sinon, réservez en ligne 24/7." },
        { question: "Combien coûte le contrôle technique ?", answer: "Voiture : 73€ en ligne (80€ standard). Moto : 70€. Utilitaire : 79€ en ligne (86€ standard)." },
        { question: "Où êtes-vous situés ?", answer: "133 Bd Aristide Briand, 93100 <a href='/le-centre' class='text-autosur-blue underline'>Montreuil-sous-Bois</a>. À moins de 5 min de <a href='/controle-technique-montreuil' class='text-autosur-blue underline'>Montreuil</a>, 5 min de <a href='/controle-technique-romainville' class='text-autosur-blue underline'>Romainville</a>, 7 min des <a href='/controle-technique-les-lilas' class='text-autosur-blue underline'>Lilas</a>, 7 min de <a href='/controle-technique-bagnolet' class='text-autosur-blue underline'>Bagnolet</a>, 10 min de <a href='/controle-technique-saint-mande' class='text-autosur-blue underline'>Saint-Mandé</a>, 10 min de <a href='/controle-technique-vincennes' class='text-autosur-blue underline'>Vincennes</a>, 8 min de <a href='/controle-technique-noisy-le-sec' class='text-autosur-blue underline'>Noisy-le-Sec</a>, 12 min de <a href='/controle-technique-rosny-sous-bois' class='text-autosur-blue underline'>Rosny-sous-Bois</a>, 10 min de <a href='/controle-technique-fontenay-sous-bois' class='text-autosur-blue underline'>Fontenay-sous-Bois</a>, 12 min de <a href='/controle-technique-bondy' class='text-autosur-blue underline'>Bondy</a>. Parking gratuit et sécurisé." },
      ]} />
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center bg-gradient-to-br from-autosur-navy via-autosur-blue to-autosur-navy overflow-hidden">
        {/* Background image overlay */}
        <div className="absolute inset-0">
          <img 
            src={heroAutosur}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-20"
            fetchPriority="high"
            loading="eager"
            decoding="async"
            width={1353}
            height={1012}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-autosur-navy/90 to-transparent" />
        </div>

        <div className="container mx-auto px-6 relative z-10 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Trustville Badge */}
              <TrustvilleBadge className="mb-6" />

              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Contrôle Technique
                <span className="block text-autosur-red">Montreuil-sous-Bois</span>
              </h1>

              <p className="font-body text-white/80 text-lg md:text-xl mb-4 max-w-lg">
                Centre de contrôle technique agréé AUTOSUR à Montreuil-sous-Bois : inspection automobile <strong className="text-white">COMPLÈTE</strong> pour voiture, moto, utilitaires et véhicules de collection.
              </p>
              <p className="font-body text-white/70 text-sm mb-2 max-w-lg">
                ✅ 20+ ans d'expertise | 343 avis clients ⭐ 4.9/5 | Équipe certifiée | Marque Préférée des Français
              </p>
              <p className="font-body text-amber-300/90 text-base font-semibold mb-2 max-w-lg">
                🚨 Vous roulez peut-être avec un défaut grave sans le savoir ?
              </p>
              <p className="font-body text-white/80 text-sm mb-4 max-w-lg">
                Détectez les défauts critiques <strong className="text-white">AVANT</strong> qu'ils ne vous coûtent cher (freins usés, suspension endommagée, éclairage défaillant, pollution excessive). Notre CT inspecte <strong className="text-white">133 points réglementaires</strong> et identifie TOUS les points faibles de votre véhicule.
              </p>
              <ul className="font-body text-white/70 text-sm mb-8 max-w-lg space-y-1">
                <li>⚡ Résultat immédiat | Procès-verbal officiel | RDV en ligne 24/7 ou sans RDV le samedi</li>
                <li>💰 Tarifs transparents : dès 73€ en ligne (économisez 7€ vs 80€ standard)</li>
                <li>🅿 Parking gratuit et sécurisé | Accès PMR | Équipe bienveillante</li>
              </ul>

              {/* CTA buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-autosur-red text-white font-heading font-semibold rounded-full hover:bg-autosur-red-light transition-all shadow-lg hover:shadow-xl"
                >
                  <img src={calendrierWhiteIcon} alt="" className="w-6 h-6" width={24} height={24} />
                  Prendre RDV
                </Link>
                <a
                  href="tel:+33149889240"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-heading font-semibold rounded-full hover:bg-white/20 transition-all border border-white/30"
                >
                  <Phone className="w-5 h-5" />
                  01 49 88 92 40
                </a>
              </div>

              {/* Quick info */}
              <div className="flex flex-wrap gap-6 text-white/70 text-sm font-body">
                <a href="https://maps.app.goo.gl/QMbfid7TsPVr8ZB76" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors">
                  <MapPin className="w-4 h-4 text-autosur-red" />
                  133, Bd Aristide Briand
                </a>
                <a href="https://controle-technique.autosur.fr/550-autosur-montreuil-sous-bois#in-nav-rdv" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors">
                  <Clock className="w-4 h-4 text-autosur-red" />
                  Lun-Ven: 8h30-18h30 | Sam: 9h-13h
                </a>
              </div>
            </motion.div>

            {/* Right - Price card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md ml-auto">
                <div className="text-center mb-6">
                  <span className="text-autosur-red font-heading font-semibold text-sm tracking-wider">CONTRÔLE TECHNIQUE VL</span>
                  <div className="mt-4">
                    <span className="font-heading text-5xl font-bold text-autosur-navy">73€</span>
                  </div>
                  <p className="text-sm text-muted-foreground font-body mt-2">
                    <span className="line-through">80€</span> À partir de (*)
                  </p>
                </div>

                <ul className="space-y-3 mb-6">
                  {["Résultat immédiat", "Parking sécurisé", "Rappel d'échéance gratuit", "Sans rendez-vous possible"].map((item) => (
                    <li key={item} className="flex items-center gap-3 font-body text-autosur-navy">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  className="block w-full py-4 bg-autosur-blue text-white font-heading font-semibold text-center rounded-full hover:bg-autosur-navy transition-colors"
                >
                  Réserver en ligne
                </Link>

                <p className="text-center text-[10px] text-muted-foreground font-body mt-3">
                  (*) Offre non cumulable
                </p>
                <p className="text-center text-xs text-muted-foreground font-body mt-1">
                  Ou appelez le 01 49 88 92 40
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vehicle Types Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-autosur-red font-heading font-semibold tracking-widest text-sm mb-4 block">
              NOS SPÉCIALITÉS
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-autosur-navy mb-4">
              Tous types de véhicules
            </h2>
            <p className="font-body text-muted-foreground max-w-2xl mx-auto">
              Centre agréé par l'État pour l'inspection automobile de tous les véhicules : voitures, motos, utilitaires, camping-cars et véhicules de collection
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4">
            {vehicleTypes.map((vehicle, index) => {
              // Sur mobile (< sm), on affiche seulement 4 éléments si showAllVehicles est false
              const isMobileHidden = index >= 4 && !showAllVehicles;
              
              return (
                <motion.div
                  key={vehicle.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={isMobileHidden ? "hidden sm:block" : ""}
                >
                  <Link
                    to={vehicle.href}
                    className="block bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg hover:border-autosur-blue/30 transition-all group text-center h-full min-h-[180px] flex flex-col items-center justify-start"
                  >
                    <div className="w-16 h-16 mb-4 bg-autosur-blue/10 rounded-2xl flex items-center justify-center group-hover:bg-autosur-blue/20 transition-colors p-3">
                      <img src={vehicle.iconSrc} alt={vehicle.title} className="w-full h-full object-contain" loading="lazy" decoding="async" width={40} height={40} />
                    </div>
                    <h3 className="font-heading font-bold text-autosur-navy mb-2">
                      {vehicle.title}
                    </h3>
                    <p className="font-body text-muted-foreground text-sm">
                      {vehicle.description}
                    </p>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          {/* Bouton Voir plus sur mobile */}
          {!showAllVehicles && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="sm:hidden mt-6 text-center"
            >
              <button
                onClick={() => setShowAllVehicles(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-autosur-blue/10 text-autosur-blue font-heading font-semibold rounded-full hover:bg-autosur-blue/20 transition-colors"
              >
                Voir plus
                <ChevronDown className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-autosur-red font-heading font-semibold tracking-widest text-sm mb-4 block">
                NOS PRESTATIONS
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-autosur-navy mb-6">
                Un contrôle technique complet et rigoureux
              </h2>
              <p className="font-body text-muted-foreground text-lg mb-8">
                Notre centre de contrôle technique Autosur à Montreuil-sous-Bois vous propose une inspection automobile complète 
                pour assurer la sécurité de votre véhicule. Procès-verbal officiel et résultat immédiat.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {services.map((service, index) => (
                  <motion.div
                    key={service}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-autosur-blue flex-shrink-0" />
                    <span className="font-body text-autosur-navy">{service}</span>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link
                  to="/prestations"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-autosur-red text-white font-heading font-semibold rounded-full hover:bg-autosur-red-light transition-colors shadow-md"
                >
                  Voir toutes nos prestations
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-autosur-navy text-white font-heading font-semibold rounded-full hover:bg-autosur-blue transition-colors"
                >
                  Prendre RDV
                </Link>
              </div>
            </motion.div>

            {/* Advantages grid */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 gap-4"
            >
              {advantages.map((advantage, index) => (
                <motion.div
                  key={advantage.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
                >
                  <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mb-4 p-2">
                    {advantage.iconSrc ? (
                      <img 
                        src={advantage.iconSrc} 
                        alt={advantage.title} 
                        className="w-8 h-8 object-contain"
                        loading="lazy"
                        decoding="async"
                        width={32}
                        height={32}
                        style={{ transform: `scale(${advantage.scale || 1})` }}
                      />
                    ) : advantage.LucideIcon ? (
                      <advantage.LucideIcon className="w-7 h-7 text-autosur-red" strokeWidth={1.5} />
                    ) : null}
                  </div>
                  <h3 className="font-heading font-bold text-autosur-navy mb-2">
                    {advantage.title}
                  </h3>
                  <p className="font-body text-muted-foreground text-sm">
                    {advantage.description}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* About teaser */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img 
                  src={facadeCentre} 
                  alt="Façade du centre de contrôle technique Autosur Montreuil-sous-Bois"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                  width={600}
                  height={450}
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-xl hidden sm:block">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-autosur-blue rounded-full flex items-center justify-center">
                    <span className="font-heading text-2xl font-bold text-white">20+</span>
                  </div>
                  <div>
                    <p className="font-heading font-bold text-autosur-navy">Années</p>
                    <p className="font-body text-muted-foreground text-sm">d'expérience</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-autosur-red font-heading font-semibold tracking-widest text-sm mb-4 block">
                NOTRE CENTRE
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-autosur-navy mb-6">
                Bienvenue chez Autosur Montreuil-sous-Bois
              </h2>
              <p className="font-body text-muted-foreground text-lg mb-6">
                Toute l'équipe de votre centre AUTOSUR est à votre disposition pour vous apporter 
                des conseils de sécurité et effectuer le CT de votre véhicule, quel que soit son type.
              </p>
              <p className="font-body text-muted-foreground mb-8">
                Votre centre agréé vous garantit un contrôle de qualité dans le respect de l'environnement, 
                avec des équipements homologués de dernière génération et une équipe de contrôleurs certifiés.
              </p>

              <Link
                to="/le-centre"
                className="inline-flex items-center gap-2 px-6 py-3 bg-autosur-navy text-white font-heading font-semibold rounded-full hover:bg-autosur-blue transition-colors"
              >
                Découvrir le centre
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Moto Banner */}
      <section className="py-12 bg-gradient-to-r from-autosur-red to-autosur-red-light relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCAyLTRzMiAyIDIgNC0yIDQtMiA0LTItMi0yLTR6Ii8+PC9nPjwvZz48L3N2Zz4=')]" />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row items-center justify-between gap-6"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <img src={motoBlueIcon} alt="Moto" className="w-9 h-9 object-contain brightness-0 invert" width={36} height={36} />
              </div>
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-white">
                  CT Moto obligatoire depuis 2024
                </h2>
                <p className="font-body text-white/80 text-sm">
                  Motos, scooters, quads, voiturettes — À partir de 70€
                </p>
              </div>
            </div>
            <Link
              to="/vehicules/motos"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-autosur-red font-heading font-semibold rounded-full hover:bg-white/90 transition-colors whitespace-nowrap shadow-lg"
            >
              <img src={calendrierRedIcon} alt="" className="w-5 h-5" width={20} height={20} />
              Prendre RDV moto
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsCarousel
        testimonials={homeTestimonials}
        title="Ce que disent nos clients"
        subtitle="343 avis Google • Note 4.9/5"
      />

      {/* CTA après avis */}
      <section className="py-8 bg-background">
        <div className="container mx-auto px-6 text-center">
          <a
            href="https://maps.app.goo.gl/QMbfid7TsPVr8ZB76"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-autosur-navy text-autosur-navy font-heading font-semibold rounded-full hover:bg-autosur-navy hover:text-white transition-all"
          >
            <Star className="w-5 h-5" />
            Voir tous les avis sur Google (343 avis ⭐ 4.9/5)
          </a>
        </div>
      </section>

      {/* FAQ Homepage */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-autosur-navy mb-8 text-center">
              Questions fréquentes
            </h2>
            <div className="space-y-4">
              {[
                { q: "Quels documents apporter pour mon CT ?", a: "Vous devez apporter : carte grise (certificat d'immatriculation) et votre permis de conduire. Pour une contre-visite, apportez également l'ancien procès-verbal de contrôle technique." },
                { q: "Combien de temps dure un contrôle technique ?", a: "Un CT prend généralement 30 à 45 minutes pour une voiture, 20-30 min pour une moto. Résultat immédiat avec procès-verbal officiel." },
                { q: "Quels défauts vont-ils trouver ?", a: "Nous inspectons 133 points réglementaires : freins, suspension, éclairage, pollution, pneus, vitres, ceintures de sécurité, etc. Les défauts critiques sont identifiés immédiatement." },
                { q: "Que faire si j'échoue mon CT ?", a: "Vous avez 2 mois pour faire une contre-visite. Celle-ci ne porte que sur les points défaillants. Contre-visite client centre à partir de 30€." },
                { q: "Puis-je faire un CT sans rendez-vous ?", a: "Oui ! Vous pouvez venir le samedi sans RDV selon disponibilités. Sinon, réservez en ligne 24/7 pour garantir votre créneau et bénéficier du tarif web." },
                { q: "Combien coûte le contrôle technique ?", a: "Voiture : 73€ en ligne (80€ standard). Moto : 70€. Utilitaire : 79€ en ligne (86€ standard). Véhicule de collection : 73€." },
                { q: "Où êtes-vous situés ?", a: "133 Bd Aristide Briand, 93100 <a href='/le-centre' class='text-autosur-blue underline'>Montreuil-sous-Bois</a>. À moins de 5 min de <a href='/controle-technique-montreuil' class='text-autosur-blue underline'>Montreuil</a>, 5 min de <a href='/controle-technique-romainville' class='text-autosur-blue underline'>Romainville</a>, 7 min des <a href='/controle-technique-les-lilas' class='text-autosur-blue underline'>Lilas</a>, 7 min de <a href='/controle-technique-bagnolet' class='text-autosur-blue underline'>Bagnolet</a>, 10 min de <a href='/controle-technique-saint-mande' class='text-autosur-blue underline'>Saint-Mandé</a>, 10 min de <a href='/controle-technique-vincennes' class='text-autosur-blue underline'>Vincennes</a>, 8 min de <a href='/controle-technique-noisy-le-sec' class='text-autosur-blue underline'>Noisy-le-Sec</a>, 12 min de <a href='/controle-technique-rosny-sous-bois' class='text-autosur-blue underline'>Rosny-sous-Bois</a>, 10 min de <a href='/controle-technique-fontenay-sous-bois' class='text-autosur-blue underline'>Fontenay-sous-Bois</a>, 12 min de <a href='/controle-technique-bondy' class='text-autosur-blue underline'>Bondy</a>. Parking gratuit et sécurisé." },
              ].map((faq, index) => (
                <details key={index} className="bg-white rounded-xl border border-gray-100 shadow-sm group">
                  <summary className="flex items-center justify-between p-5 cursor-pointer font-heading font-semibold text-autosur-navy text-sm hover:text-autosur-blue transition-colors">
                    {faq.q}
                    <ChevronDown className="w-5 h-5 text-muted-foreground group-open:rotate-180 transition-transform flex-shrink-0 ml-4" />
                  </summary>
                  <div className="px-5 pb-5 pt-0">
                    <p className="font-body text-muted-foreground text-sm" dangerouslySetInnerHTML={{ __html: faq.a }} />
                  </div>
                </details>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/faq"
                className="inline-flex items-center gap-2 text-autosur-blue font-heading font-semibold hover:text-autosur-navy transition-colors"
              >
                Voir toutes les questions
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-autosur-red font-heading font-semibold tracking-widest text-sm mb-4 block">
              ACTUALITÉS
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-autosur-navy mb-4">
              Nos derniers articles
            </h2>
            <p className="font-body text-muted-foreground max-w-2xl mx-auto">
              Conseils, réglementation et actualités du contrôle technique automobile
            </p>
          </motion.div>

          <Suspense fallback={<div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 min-h-[300px]" />}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {articles.slice(0, 4).map((article, index) => (
                <ArticleCard key={article.slug} article={article} index={index} />
              ))}
            </div>
          </Suspense>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-10"
          >
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-autosur-blue font-heading font-semibold hover:text-autosur-navy transition-colors"
            >
              Voir tous les articles
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Location, Hours & Partners Section */}
      <Suspense fallback={<div className="min-h-[400px]" />}>
        <LocationSection />
      </Suspense>

      {/* CTA Section */}
      <Suspense fallback={<div className="min-h-[200px]" />}>
        <CTASection 
          title="Prêt ? Réservez votre CT maintenant"
          subtitle="Roulez l'esprit tranquille. Prenez rendez-vous en ligne et bénéficiez du tarif web."
        />
      </Suspense>
    </SiteLayout>
  );
};

export default HomePage;
